import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useInfiniteDesigns, useToggleFavoriteDesign } from '@/api/designs'
import AppNavbar from '@/components/app-navbar'

import type { Design } from '@/api/designs'
import Lightbox from './components/lightbox'
import LayoutToggle from './components/layout-toggle'
import MasonryGrid from './components/masonry-grid'
import SymmetricGrid from './components/symmetric-grid'
import LargeCardsGrid from './components/large-cards-grid'
import { useLayoutMode } from './hooks/use-layout-mode'
import '@/components/ambient/animations.css'

import type { LayoutMode } from './types'

function GalleryPage() {
  const [lightboxDesign, setLightboxDesign] = useState<Design | null>(null)

  const { layoutMode, setLayoutMode } = useLayoutMode()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteDesigns({})
  const { mutate: toggleFavorite } = useToggleFavoriteDesign()

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const designs = data?.pages.flatMap((p) => p.items) ?? []

  const handleToggleFavorite = useCallback(
    (designId: string, isFavorite: boolean) => {
      toggleFavorite({ designId, isFavorite })
    },
    [toggleFavorite]
  )

  const handleImageClick = useCallback((design: Design) => {
    setLightboxDesign(design)
  }, [])

  return (
    <Box position="relative" minH="100vh" bg="bg.primary">
      <Box>
        <AppNavbar />

        <Flex
          h="48px"
          px={{ base: '16px', md: '32px' }}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="border.subtle"
        >
          <Text textStyle="bodySmall" color="text.tertiary">
            {designs.length} designs
          </Text>
          <LayoutToggle
            layoutMode={layoutMode}
            onChangeLayout={setLayoutMode}
          />
        </Flex>

        <Box py="40px">
          <AnimatePresence mode="wait">
            <motion.div
              key={layoutMode}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <GridForMode
                layoutMode={layoutMode}
                designs={designs}
                onImageClick={handleImageClick}
                onToggleFavorite={handleToggleFavorite}
              />
            </motion.div>
          </AnimatePresence>

          <Box ref={sentinelRef} h="1px" />

          {isFetchingNextPage && (
            <Box display="flex" justifyContent="center" py="40px">
              <Spinner size="lg" color="text.tertiary" />
            </Box>
          )}
        </Box>
      </Box>

      {lightboxDesign && (
        <Lightbox design={lightboxDesign} onClose={() => setLightboxDesign(null)} />
      )}

    </Box>
  )
}

function GridForMode({
  layoutMode,
  designs,
  onImageClick,
  onToggleFavorite
}: {
  layoutMode: LayoutMode
  designs: Design[]
  onImageClick: (design: Design) => void
  onToggleFavorite: (designId: string, isFavorite: boolean) => void
}) {
  const gridProps = { designs, onImageClick, onToggleFavorite }

  switch (layoutMode) {
    case 'grid':
      return <SymmetricGrid {...gridProps} />
    case 'large':
      return <LargeCardsGrid {...gridProps} />
    default:
      return <MasonryGrid {...gridProps} />
  }
}

export default GalleryPage
